<template>
  <div class="coverage-screen">
    <commonModal
      :show="showDeductibleWarningModal"
      :actionButton="true"
      :options="{
        title: 'Decline Coverage',
        description: declineModalText,
        buttonLabel: 'OK'
      }"
      @close="clickedCloseOnModal"
    ></commonModal>
    <form>
      <fieldset>
        <StateCoveragesForm
          v-if="hasStateCoverageConfig"
          :selectedState="selectedState"
          :coverageStepIndex="coverageStepIndex"
          :validationOptions="validationOptions"
          v-bind:coverageForm="form"
          :calculated="calculated"
          :isUnlimited="isUnlimited"
          :singlePayPayment="singlePayPayment"
          :monthlyPayDownPayment="monthlyPayDownPayment"
          :radios="radios"
          :uninsuredCoverageTypeTooltip="uninsuredCoverageTypeTooltip"
          @updateField="updateField"
          @previousVehicleClick="previousVehicleClick"
          @nextVehicleClick="nextVehicleClick"
          @updateQuoteResponseToNull="updateQuoteResponseToNull"
          @toggleUnlimited="toggleUnlimited"
        />
        <div v-else>
          We're having trouble loading this page, please go back and try again,
          or call our Customer Care team at
          <nobr
            ><a
              style="text-decoration:underline;color:#fff"
              href="tel:1-800-400-4128"
              >800-400-4128</a
            ></nobr
          >
        </div>
        <div class="button-container columns is-tablet">
          <div class="button-left-align column is-6">
            <button
              @click="backButtonNavigation"
              type="button"
              v-if="currentStepNumber > 1 && currentStepNumber < 5"
              class="button-primary button-primary--black -spacing-static-b back-button"
            >
              <span class="button-primary__icon icon icon--arrow-right-hair">
                <span class="loader-base button-primary__loader"></span>
              </span>

              <span class="button-primary__label">Back</span>
            </button>

            <button
              @click="recalculate"
              type="button"
              class="recalculate button-primary button-primary--black -spacing-static-b calculate-button"
              :class="{
                disabled: stateCoverageContinueDisableRules
              }"
              :disabled="stateCoverageContinueDisableRules"
            >
              <span
                class="button-primary__icon icon icon--arrow-right-hair"
                :class="{
                  disabled__button__color: stateCoverageContinueDisableRules
                }"
              >
                <span class="loader-base button-primary__loader"></span>
              </span>

              <span
                class="button-primary__label"
                :class="{
                  disabled__button__color: stateCoverageContinueDisableRules
                }"
                >Calculate</span
              >
            </button>
          </div>
          <div class="button-right-align column is-6">
            <button
              @click="navigateForward()"
              type="button"
              class="button-primary button-primary--black -spacing-static-b"
              v-if="currentStepNumber !== 7"
              :class="{
                disabled: !quoteResponse
              }"
              :disabled="quoteResponse === null || quoteResponse === undefined"
            >
              <span
                class="button-primary__icon icon icon--arrow-right-hair"
                :class="{
                  disabled__button__color: stateCoverageContinueDisableRules
                }"
              >
                <span class="loader-base button-primary__loader"></span>
              </span>

              <span
                class="button-primary__label"
                :class="{
                  disabled__button__color:
                    stateCoverageContinueDisableRules || errors.count() > 0
                }"
              >
                Finalize
              </span>
            </button>
          </div>
        </div>
      </fieldset>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { canUseAdvertising, safeGtag } from '@/utils/tracking';
import moment from 'moment';
import * as _ from 'lodash';
import { setTimeout } from 'timers';
import commonModal from '../shared/commonModal';
import StateCoveragesForm from '@/components/quote/StateCoverages/StateCoveragesForm.vue';
import { STATE_COVERAGE_CONFIGS } from '@/components/quote/StateCoverages/stateCoverageConfigs';
import { monthlyDownPayment, paidInFullDownPayment } from '@/utils/downPayment';

export default {
  props: {
    wizardData: {
      type: Object,
      required: true
    },
    currentStepNumber: {
      type: Number
    },
    isLastStep: {
      type: Boolean
    }
  },
  data: () => ({
    name: 'Your Coverage',
    calculated: true,
    isUnlimited: false,
    radios: {
      toggleForCoverageType: [
        {
          key: 'Reduced By',
          value: 'R'
        },
        {
          key: 'Added On',
          value: 'A'
        }
      ]
    },
    uninsuredCoverageTypeTooltip:
      '<b>Reduced By: </b>Porsche Auto Insurance will cover up to the difference between the at-fault driver’s liability coverage and the amount of Uninsured/Underinsured Property Damage coverage on your plan.<br/> <b>Added On: </b>Porsche Auto Insurance will cover damages in addition to the at-fault driver’s liability coverage, up to the amount of Uninsured/Underinsured Property Damage coverage on your plan.',
    validationOptions: {
      date: {
        date: true,
        datePattern: ['m', 'd', 'Y'],
        delimiter: '/'
      },
      effectiveDateMin: moment()
        .startOf('day')
        .toDate(),
      effectiveDateMax: moment()
        .add(30, 'd')
        .toDate()
    },
    form: {
      bodilyInjury: null,
      propertyDamage: null,
      comprehensiveDeductible: [],
      collisionDeductible: [],
      propertyDamageDeductible: [],
      uninsuredCoverageType: 'R',
      uninsuredBodilyInjury: null,
      underinsuredMotorist: null,
      uninsuredPropertyDamage: null,
      personalInjuryProtection: null,
      pipDeductible: null,
      pipDeductibleType: null,
      medicalPayments: null,
      rentalReimbursement: [],
      combinedFirstParty: null,
      accidentalDeath: null,
      funeralBenefits: null,
      incomeLoss: null,
      extraBenefits: null,
      tortOption: null,
      policyEffectiveDate: moment().format('MM/DD/YYYY')
    },
    coverageStepIndex: 0,
    vehicleImgBaseUrl: process.env.VUE_APP_PORSCHE_PIC_BASE_URL,
    showDeductibleWarningModal: false
  }),
  components: {
    StateCoveragesForm,
    commonModal
  },
  activated() {
    this.coverageStepIndex = 0;
  },
  mounted() {
    this.floodlightQuote();
    this.form.bodilyInjury = null;
    if (this.quoteForm && this.quoteForm.vehicles) {
      this.quoteForm.vehicles.forEach(veh => {
        veh.unlimitedVehicle = false;
      });
    }
    if (this.selectedState === 'GA') {
      this.personalInjuryProtection = this.personalInjuryProtection || '$2,000';
    }
    if (this.selectedState === 'FL') {
      this.form.personalInjuryProtection = this.selectsCoverage[
        this.selectedState
      ].personalInjuryProtection[0];
      this.form.pipDeductible =
        this.form.pipDeductible ||
        this.selectsCoverage[this.selectedState].pipDeductible[2];
      this.form.pipDeductibleType =
        this.form.pipDeductibleType ||
        this.selectsCoverage[this.selectedState].pipDeductibleType[1];
      if (
        !this.selectsCoverage.FL.bodilyInjury.includes(
          'I decline this coverage'
        )
      ) {
        this.selectsCoverage.FL.bodilyInjury.push('I decline this coverage');
      }
    }
    if (!_.isEmpty(this.quoteForm.coverage)) {
      this.form = _.cloneDeep(this.quoteForm.coverage);
      this.$validator
        .validate('policyEffectiveDate', this.form.policyEffectiveDate)
        .then(result => {
          if (!result) {
            setTimeout(() => {
              this.$el
                .querySelector('[name="policyEffectiveDate"]')
                .scrollIntoView();
            }, 100);
          }
        });
      this.coverageStepIndex = 0;

      if (
        this.quoteForm.vehicles[this.coverageStepIndex].leaseOwnFinance
          .value === 'owned'
      ) {
        if (
          this.selectsCoverage[this.selectedState].comprehensiveDeductible[
            this.selectsCoverage[this.selectedState].comprehensiveDeductible
              .length - 1
          ] !== 'I decline this coverage'
        ) {
          this.selectsCoverage[this.selectedState].comprehensiveDeductible.push(
            'I decline this coverage'
          );
        }
        if (
          this.selectsCoverage[this.selectedState].collisionDeductible[
            this.selectsCoverage[this.selectedState].collisionDeductible
              .length - 1
          ] !== 'I decline this coverage'
        ) {
          this.selectsCoverage[this.selectedState].collisionDeductible.push(
            'I decline this coverage'
          );
        }
      }

      if (
        this.form['comprehensiveDeductible'] &&
        this.form['comprehensiveDeductible'].length <= 1
      ) {
        this.setVehicleCoverages('comprehensiveDeductible');
      }
      if (
        this.form['collisionDeductible'] &&
        this.form['collisionDeductible'].length <= 1
      ) {
        this.setVehicleCoverages('collisionDeductible');
      }
      if (
        this.selectedState === 'GA' &&
        this.form['propertyDamageDeductible'] &&
        this.form['propertyDamageDeductible'].length <= 1
      ) {
        this.setVehicleCoverages('propertyDamageDeductible');
      }
    }
  },
  computed: {
    ...mapGetters([
      'quoteResponse',
      'quoteForm',
      'quoteRequest',
      'vehicleImageResponse',
      'selectsAgencyCodes',
      'selectsCoverage',
      'authStatus'
    ]),
    unlimitedState() {
      const config = STATE_COVERAGE_CONFIGS[this.selectedState];
      return !!(config && config.unlimited);
    },
    policy() {
      return (
        (this.quoteResponse &&
          this.quoteResponse.policies &&
          this.quoteResponse.policies[0]) ||
        null
      );
    },
    // Both down payments come from src/utils/downPayment.js, which the quote
    // and payment screens share. They are rated figures only — there is no
    // local calculation to fall back on, so an empty response renders a
    // placeholder.
    monthlyPayDownPayment() {
      const downPayment = monthlyDownPayment(this.policy);
      return downPayment === null ? '' : downPayment.toFixed(2);
    },
    singlePayPayment() {
      const downPayment = paidInFullDownPayment(
        this.policy,
        this.selectedState
      );
      return downPayment === null ? '' : downPayment.toFixed(2);
    },
    declineModalText() {
      if (this.selectedState === 'FL') {
        return `Comprehensive coverage is for damage to your vehicle caused by something other than a
            collision, such as theft, flood, vandalism, or fire. Collision coverage is for damage to your vehicle
            due to a collision. Collision Coverage may not be purchased without Comprehensive Coverage. If you decline these coverages,
            your policy will not cover damage to your vehicle for any reason.`;
      } else {
        return `Comprehensive coverage is for damage to your vehicle caused by something other than a
        collision, such as theft, flood, vandalism, or fire. Collision coverage is for damage to your vehicle
        due to a collision. These coverages must be purchased together. If you decline these coverages,
        your policy will not cover damage to your vehicle for any reason.`;
      }
    },
    hasStateCoverageConfig() {
      return !!STATE_COVERAGE_CONFIGS[this.selectedState];
    },
    stateCoverageContinueDisableRules() {
      const config = STATE_COVERAGE_CONFIGS[this.selectedState];
      if (config && config.continueDisabled) {
        return config.continueDisabled(this.form, this.quoteResponse);
      }
      return !(
        this.form.bodilyInjury &&
        this.form.comprehensiveDeductible.length &&
        this.form.collisionDeductible.length &&
        this.form.policyEffectiveDate &&
        this.isDisabled
      );
    },
    selectedState() {
      if (
        this.quoteForm.info.garagingState &&
        this.quoteForm.info.isGarageSameAsMailing.value !== 1
      ) {
        return _.cloneDeep(this.quoteForm.info.garagingState.value);
      } else if (this.quoteForm.info.state) {
        return _.cloneDeep(this.quoteForm.info.state.value);
      } else {
        return '';
      }
    },
    isDisabled() {
      if (this.quoteResponse == null) {
        return true;
      } else {
        return false;
      }
    },
    hasUninsuredPropertyDamageProtection() {
      return (
        this.form.uninsuredBodilyInjury !== 'I decline this coverage' &&
        this.form.uninsuredBodilyInjury !== null &&
        this.form.propertyDamageDeductible[this.coverageStepIndex] !==
          'Coverage Not Applicable'
      );
    },
    postPayload() {
      let form = this.quoteForm;
      let policyEffectiveDate = moment(
        this.form.policyEffectiveDate,
        'MM/DD/YYYY'
      );
      let policyExpiryDate = moment(policyEffectiveDate).add(6, 'months');
      let drivers = form.drivers.map((driver, index) => {
        if (index === 0) {
          return {
            status: 'RATED',
            licenseState: driver.licenseState.value || form.info.state.value,
            licenseStatus: driver.licenseStatus.value,
            licenseNum: driver.licenseNum,
            violations: driver.accidentsConvictions.value,
            relation: 'INSURED',
            isMarried: driver.maritalStatus === 'Married',
            dob: moment(driver.dateOfBirth, 'MM/DD/YYYY').format(),
            lastName: driver.lastName,
            firstName: driver.firstName,
            gender: driver.gender.value,
            isExcluded: false,
            educationCode:
              (driver.levelOfEducation && driver.levelOfEducation.value) ||
              'CollegeGraduated',
            occupationCode:
              driver.occupationCode && driver.occupationCode.toString()
          };
        } else if (driver.relation === 'Spouse') {
          if (driver.isExcluded.value) {
            return {
              status: 'LIST',
              relation: driver.relation.toUpperCase(),
              isMarried: true,
              dob: moment(driver.dateOfBirth, 'MM/DD/YYYY').format(),
              lastName: driver.lastName,
              firstName: driver.firstName,
              middleInitial: null,
              gender: driver.gender.value,
              isExcluded: driver.isExcluded.value,
              // The line below needs to be updated to set the correct person's education code
              educationCode:
                (driver.levelOfEducation && driver.levelOfEducation.value) ||
                'CollegeGraduated',
              occupationCode:
                driver.occupationCode && driver.occupationCode.toString()
            };
          } else {
            return {
              status: 'RATED',
              licenseState: driver.licenseState.value,
              licenseStatus: driver.licenseStatus.value,
              licenseNum: driver.licenseNum,
              violations: driver.accidentsConvictions.value,
              relation: driver.relation.toUpperCase(),
              isMarried: true,
              dob: moment(driver.dateOfBirth, 'MM/DD/YYYY').format(),
              lastName: driver.lastName,
              firstName: driver.firstName,
              middleInitial: null,
              gender: driver.gender.value,
              isExcluded: driver.isExcluded.value || false,
              educationCode: driver.levelOfEducation.value,
              occupationCode:
                driver.occupationCode && driver.occupationCode.toString()
            };
          }
        } else {
          return {
            status: 'RATED',
            licenseState: driver.licenseState.value,
            licenseStatus: driver.licenseStatus.value,
            licenseNum: driver.licenseNum,
            isMarried: driver.maritalStatus === 'Married',
            violations: driver.accidentsConvictions.value,
            relation: driver.relation.toUpperCase(),
            dob: moment(driver.dateOfBirth, 'MM/DD/YYYY').format(),
            lastName: driver.lastName,
            firstName: driver.firstName,
            middleInitial: null,
            gender: driver.gender.value,
            isExcluded: false,
            educationCode: driver.levelOfEducation.value,
            occupationCode:
              driver.occupationCode && driver.occupationCode.toString()
          };
        }
      });
      drivers.forEach((thisDrive, index) => {
        const defaultCode = form.info.state.value === 'FL' ? '125' : 'AF7';

        if (!thisDrive.occupationCode) {
          if (!form.drivers[index].occupationCode) {
            let foundOccupation =
              form.drivers[index].occupation &&
              form.drivers[index].occupation.value &&
              form.drivers[index].occupation.value.toString();
            thisDrive.occupationCode = foundOccupation || defaultCode;
          } else {
            thisDrive.occupationCode =
              form.drivers[index].occupationCode || defaultCode;
          }
        }
      });
      const vueInstance = this;
      let vehicles = form.vehicles.map((vehicle, index) => {
        let vehicleObj = {
          mileage: {
            usageType: vehicle.purpose.value,
            miles:
              parseInt(vehicle.miles) > 14600 ? 14600 : parseInt(vehicle.miles)
          },
          billingType: 'per_mile',
          vin: _.toUpper(vehicle.vin),
          lengthOfOwnership:
            (vehicle.lengthOfOwnership && vehicle.lengthOfOwnership.value) ||
            '<1',
          aebCode: 'YES',
          blindSpotCode: 'YES',
          statedValue: Number(
            vehicle.statedValue.toString().replace(/[^0-9]/g, '')
          ),
          financing: vehicle.leaseOwnFinance.value.toUpperCase(),
          priorDamage: vehicle.priorDamage.value,
          coverageLimits: []
        };
        if (
          vueInstance.form.comprehensiveDeductible[index] &&
          vueInstance.form.comprehensiveDeductible[index] !==
            'I decline this coverage'
        ) {
          vehicleObj.coverageLimits.push({
            name: 'COMP',
            type: 'VEHICLE',
            indexNumber: 0,
            limit: this.stripFormat(
              vueInstance.form.comprehensiveDeductible[index]
                ? vueInstance.form.comprehensiveDeductible[index]
                : this.setValueForVehicleArray('comprehensiveDeductible', index)
            )
          });
        }
        if (
          vueInstance.form.collisionDeductible[index] &&
          vueInstance.form.collisionDeductible[index] !==
            'I decline this coverage'
        ) {
          vehicleObj.coverageLimits.push({
            name: 'COLL',
            type: 'VEHICLE',
            indexNumber: 0,
            limit: this.stripFormat(
              vueInstance.form.collisionDeductible[index]
                ? vueInstance.form.collisionDeductible[index]
                : this.setValueForVehicleArray('collisionDeductible', index)
            )
          });
        }
        if (
          vueInstance.form.rentalReimbursement[index] &&
          vueInstance.form.rentalReimbursement[index] !==
            'I decline this coverage'
        ) {
          vehicleObj.coverageLimits.push({
            name: 'RENT',
            type: 'VEHICLE',
            indexNumber: 0,
            limit: this.stripFormat(
              vueInstance.form.rentalReimbursement[index]
                ? vueInstance.form.rentalReimbursement[index]
                : this.setValueForVehicleArray('rentalReimbursement', index)
            )
          });
        }
        if (
          this.selectedState === 'GA' &&
          this.hasUninsuredPropertyDamageProtection
        ) {
          vehicleObj.coverageLimits.push(
            {
              name: 'UMUIMPDDeductible',
              type: 'VEHICLE',
              indexNumber: 0,
              limit: this.stripFormat(
                this.form.propertyDamageDeductible[this.coverageStepIndex]
              )
            },
            {
              name: 'UMUIMPD',
              type: 'VEHICLE',
              indexNumber: 0,
              limit: this.stripFormat(vueInstance.form.uninsuredPropertyDamage)
            }
          );
        }
        if (
          this.selectedState === 'OR' ||
          this.selectedState === 'IL' ||
          this.selectedState === 'CO'
        ) {
          vehicleObj.coverageLimits.push({
            name: 'GLASS',
            type: 'VEHICLE',
            indexNumber: 0,
            limit: 'YES'
          });
        }
        if (
          this.selectedState === 'OH' &&
          vueInstance.form.uninsuredPropertyDamage &&
          vueInstance.form.uninsuredPropertyDamage !== 'I decline this coverage'
        ) {
          vehicleObj.coverageLimits.push({
            name: 'UMUIMPD',
            type: 'VEHICLE',
            indexNumber: 0,
            limit: this.stripFormat(vueInstance.form.uninsuredPropertyDamage)
          });
        }
        if (
          vehicleObj.coverageLimits.length > 0 &&
          vehicleObj.coverageLimits[0].limit === 'I decline this coverage'
        ) {
          vehicleObj.coverageLimits.splice(0, 3);
        }
        return vehicleObj;
      });
      let referredAgencyCode = this.selectsAgencyCodes.find(code => {
        return (
          code.name.toUpperCase() === form.info.hearAbout.value.toUpperCase()
        );
      });
      if (process.env.VUE_APP_ENV !== 'Production') {
        referredAgencyCode = this.selectsAgencyCodes.find(code => {
          return (
            code.name.toUpperCase() ===
              form.info.hearAbout.value.toUpperCase() && code.dev
          );
        });
      }
      let priorCoverageValue = form.drivers[0].hasAutoInsurance.value;
      if (
        (form.drivers[0].hasAutoInsurance.value === 1 ||
          form.drivers[0].hasAutoInsurance.value === 3) &&
        form.drivers[0].recentIssuerLength.value === 0
      ) {
        priorCoverageValue = 0;
      }
      let payload = {
        returnUrl: window.location.origin + '/receive-a-quote',
        rateCall: 'APIRC2',
        estimatedCreditScore: 'Excellent',
        creditScore: 0,
        agencyCode:
          !_.isEmpty(referredAgencyCode) && !_.isEmpty(referredAgencyCode)
            ? referredAgencyCode.code
            : process.env.VUE_APP_AGENCY_CODE,
        previousCompany:
          (form.info.previousCompany && form.info.previousCompany.value) ||
          'OTHER STANDARD',
        insuredFirstName: form.info.firstName,
        insuredLastName: form.info.lastName,
        insuredMiddleInitial: null,
        emailAddress: form.drivers[0].emailAddress,
        addresses: [
          {
            state: form.info.state.value,
            zip: form.info.zipCode,
            city: form.info.city,
            streetName: form.info.streetAddressOne,
            type: 'Mailing'
          }
        ],
        ratedState: form.info.state.value,
        priorCoverage: priorCoverageValue,
        priorYearsInsured:
          form.drivers[0].recentIssuerLength !== undefined &&
          form.drivers[0].recentIssuerLength !== null
            ? form.drivers[0].recentIssuerLength.value
            : 0,
        term: 6,
        effectiveDate: policyEffectiveDate.format(),
        referral: form.info.hearAbout.value,
        expirationDate: policyExpiryDate.format(),
        drivers: drivers,
        vehicles: vehicles,
        limits: [],
        phoneNumbers: [
          {
            type: 'Other',
            number: form.info.preferredContactNumber
          }
        ],
        isGarageSameAsMailing: form.info.isGarageSameAsMailing.value === 1,
        isHomeOwner: form.drivers[0].homeRentalStatus.value,
        isInPorscheClubOfAmerica:
          form.info.isPCAMember.value || form.info.state.value === 'FL',
        calculateUnlimited: true
      };
      if (
        this.form.bodilyInjury &&
        this.form.bodilyInjury !== 'I decline this coverage'
      ) {
        payload.limits.push({
          name: 'BI',
          type: 'POLICY',
          indexNumber: 0,
          limit: this.stripFormat(this.form.bodilyInjury)
        });
      }
      if (
        this.form.propertyDamage &&
        this.form.propertyDamage !== 'I decline this coverage'
      ) {
        payload.limits.push({
          name: 'PD',
          type: 'POLICY',
          indexNumber: 0,
          limit: this.stripFormat(this.form.propertyDamage)
        });
      }
      if (
        this.form.personalInjuryProtection &&
        this.form.personalInjuryProtection !== 'I decline this coverage'
      ) {
        if (this.selectedState !== 'FL') {
          payload.limits.push({
            name:
              this.selectedState === 'OR' ||
              this.selectedState === 'TX' ||
              this.selectedState === 'UT'
                ? 'PIP'
                : 'MP',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.personalInjuryProtection)
          });
        }
      }

      if (this.form.tortOption) {
        payload.tortOption =
          this.form.tortOption && this.form.tortOption.toLowerCase() === 'full'
            ? 'F'
            : 'L';
      } else if (this.selectedState === 'PA') {
        payload.tortOption = 'L';
      }

      if (this.selectedState === 'AZ') {
        payload.lengthOfResidency =
          (form.info.lengthOfResidency && form.info.lengthOfResidency.value) ||
          '12+';
        if (
          this.form.uninsuredBodilyInjury !== null &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMBI',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredBodilyInjury)
          });
        }
        if (
          this.form.underinsuredMotorist !== null &&
          this.form.underinsuredMotorist.toLowerCase() !==
            'i decline this coverage'
        ) {
          payload.limits.push({
            name: 'UIM',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.underinsuredMotorist)
          });
        }
      }
      if (this.selectedState === 'CA') {
        if (
          this.form.uninsuredBodilyInjury &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMBI',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredBodilyInjury)
          });
          if (
            this.form.uninsuredPropertyDamage &&
            !!this.form.uninsuredPropertyDamage &&
            this.form.uninsuredPropertyDamage !== 'I decline this coverage'
          ) {
            payload.limits.push({
              name: 'UMPD',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredPropertyDamage)
            });
          }
        }
      }
      if (this.selectedState === 'CO') {
        if (
          this.form.uninsuredBodilyInjury &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMUIMBI',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredBodilyInjury)
          });
          payload.vehicles.forEach((v, index) => {
            let vehicleHasColl = v.coverageLimits.find(c => c.name === 'COLL');
            if (
              this.form.propertyDamageDeductible &&
              this.form.propertyDamageDeductible.length > 0 &&
              this.form.propertyDamageDeductible[index] &&
              this.form.propertyDamageDeductible[index] !==
                'I decline this coverage' &&
              !vehicleHasColl
            ) {
              const strippedLimit = this.stripFormat(
                this.form.propertyDamageDeductible[index]
              );
              const formattedLimit = limitValue => {
                switch (limitValue) {
                  case '100':
                    return 'ACV W/ 100 DED';
                  case '250':
                    return 'ACV W/ 250 DED';
                  case '500':
                    return 'ACV W/ 500 DED';
                  case '1000':
                    return 'ACV W/ 1000 DED';
                  case '2500':
                    return 'ACV W/ 2500 DED';
                  default:
                    return 'ACV W/ 100 DED';
                }
              };
              v.coverageLimits.push({
                name: 'UMUIMPD',
                type: 'VEHICLE',
                indexNumber: 0,
                limit: formattedLimit(strippedLimit)
              });
            }
          });
        }
      }
      if (this.selectedState === 'FL') {
        payload.limits.push({
          name: 'PIP',
          type: 'POLICY',
          indexNumber: 0,
          limit: '10000'
        });
        if (
          this.form.medicalPayments !== null &&
          this.form.medicalPayments !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'MP',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.medicalPayments)
          });
        }
        if (
          this.form.bodilyInjury !== null &&
          this.form.bodilyInjury !== 'I decline this coverage' &&
          this.form.uninsuredBodilyInjury !== null &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push(
            {
              name: 'UMBI',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredBodilyInjury)
            },
            {
              name: 'UM',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.form.uninsuredCoverageType === 'Stacked' ? 'S' : 'NS'
            }
          );
        }
        if (
          this.form.pipDeductible !== null &&
          this.form.pipDeductible !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'PIPDeductible',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.pipDeductible)
          });
        }
        if (
          this.form.pipDeductibleType !== null &&
          this.form.pipDeductibleType !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'PIPDeductibleType',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.form.pipDeductibleType.includes('Relative')
              ? 'NIRR'
              : 'NI'
          });
        }
        if (
          this.form.pipDeductibleType !== null &&
          this.form.pipDeductibleType !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'WORKLOSS',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.form.pipDeductibleType.includes('Included')
              ? 'INCLUDE'
              : 'EXCLUDE'
          });
        }
      }
      if (this.selectedState === 'GA') {
        if (
          this.form.uninsuredBodilyInjury !== null &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          if (this.form.uninsuredBodilyInjury) {
            payload.limits.push({
              name: 'UMUIMBI',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredBodilyInjury)
            });
          }
          payload.limits.push({
            name: 'UM',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.form.uninsuredCoverageType
          });
        }
      }
      if (this.selectedState === 'IL' || this.selectedState === 'OR') {
        if (
          this.form.uninsuredBodilyInjury &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMUIMBI',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredBodilyInjury)
          });
        }
        if (
          !!this.form.uninsuredPropertyDamage &&
          this.form.uninsuredPropertyDamage !== 'I decline this coverage'
        ) {
          if (this.selectedState === 'IL') {
            payload.vehicles.forEach(v => {
              v.coverageLimits.push({
                name: 'UMUIMPD',
                type: 'VEHICLE',
                indexNumber: 0,
                limit: this.stripFormat(this.form.uninsuredPropertyDamage)
              });
            });
          } else {
            payload.limits.push({
              name: 'UMPD',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredPropertyDamage)
            });
          }
        }
      }
      if (this.selectedState === 'OH') {
        payload.lengthOfResidency =
          (form.info.lengthOfResidency && form.info.lengthOfResidency.value) ||
          '12+';
        payload.householdCount = Number.parseInt(form.info.householdCount);
        if (
          this.form.uninsuredBodilyInjury !== null &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          if (this.form.uninsuredBodilyInjury) {
            payload.limits.push({
              name: 'UMUIMBI',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredBodilyInjury)
            });
          }
        }
      }
      if (this.selectedState === 'PA') {
        payload.lengthOfResidency = form.info.lengthOfResidency.value || '12+';
        if (
          !!this.form.uninsuredBodilyInjury &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push(
            {
              name: 'UMBI',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredBodilyInjury)
            },
            {
              name: 'UM',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.form.uninsuredCoverageType === 'Stacked' ? 'S' : 'NS'
            }
          );
        }
        if (
          !!this.form.underinsuredMotorist &&
          this.form.underinsuredMotorist !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UIM',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.underinsuredMotorist)
          });
        }
        if (
          this.form.combinedFirstParty &&
          this.form.combinedFirstParty !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'FPB',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat('177500')
          });
        } else {
          if (
            this.form.accidentalDeath &&
            this.form.accidentalDeath !== 'I decline this coverage'
          ) {
            payload.limits.push({
              name: 'ADND',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.accidentalDeath)
            });
          }
          if (
            this.form.funeralBenefits &&
            this.form.funeralBenefits !== 'I decline this coverage'
          ) {
            payload.limits.push({
              name: 'FNRL',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.funeralBenefits)
            });
          }
          if (
            this.form.incomeLoss &&
            this.form.incomeLoss !== 'I decline this coverage'
          ) {
            payload.limits.push({
              name: 'INCLOSS',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.incomeLoss)
            });
          }
        }
        if (
          this.form.extraBenefits &&
          this.form.extraBenefits !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'MEDEX',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat('1100000')
          });
        }
      }
      if (this.selectedState === 'TN') {
        payload.lengthOfResidency =
          (form.info.lengthOfResidency && form.info.lengthOfResidency.value) ||
          '12+';
        if (
          this.form.uninsuredBodilyInjury !== null &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMBI',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredBodilyInjury)
          });
          if (
            this.form.uninsuredPropertyDamage !== null &&
            this.form.uninsuredPropertyDamage !== 'I decline this coverage'
          ) {
            payload.limits.push({
              name: 'UMPD',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredPropertyDamage)
            });
          }
        }
      }
      if (this.selectedState === 'TX') {
        if (
          this.form.uninsuredBodilyInjury !== null &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          if (this.form.uninsuredBodilyInjury) {
            payload.limits.push({
              name: 'UMBI',
              type: 'POLICY',
              indexNumber: 0,
              limit: this.stripFormat(this.form.uninsuredBodilyInjury)
            });
          }
        }
        if (
          this.form.uninsuredPropertyDamage !== null &&
          this.form.uninsuredPropertyDamage !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMPD',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredPropertyDamage)
          });
        }
      }
      if (this.selectedState === 'UT') {
        if (
          this.form.personalInjuryProtection !== null &&
          this.form.personalInjuryProtection !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'WORKLOSS',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.form.personalInjuryProtection.includes('with wage')
              ? 'INCLUDE'
              : 'EXCLUDE'
          });
        }
        if (
          this.form.uninsuredBodilyInjury &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMBI',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredBodilyInjury)
          });
          payload.vehicles.forEach(v => {
            v.coverageLimits.push({
              name: 'UMUIMPD',
              type: 'VEHICLE',
              indexNumber: 0,
              limit: '3500'
            });
          });
          payload.vehicles.forEach((v, index) => {
            if (
              this.form.propertyDamageDeductible &&
              this.form.propertyDamageDeductible.length > 0 &&
              this.form.propertyDamageDeductible[index] &&
              this.form.propertyDamageDeductible[index] !==
                'I decline this coverage'
            ) {
              v.coverageLimits.push({
                name: 'UMUIMPD',
                type: 'VEHICLE',
                indexNumber: 0,
                limit: this.stripFormat(
                  this.form.propertyDamageDeductible[index]
                )
              });
            }
          });
        }
        if (
          this.form.underinsuredMotorist &&
          this.form.underinsuredMotorist !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UIM',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.underinsuredMotorist)
          });
        }
      }
      if (this.selectedState === 'WI') {
        payload.lengthOfResidency =
          (form.info.lengthOfResidency && form.info.lengthOfResidency.value) ||
          '12+';
        if (
          this.form.uninsuredBodilyInjury !== null &&
          this.form.uninsuredBodilyInjury !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UMBI',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.uninsuredBodilyInjury)
          });
        }
        if (
          this.form.underinsuredMotorist !== null &&
          this.form.underinsuredMotorist !== 'I decline this coverage'
        ) {
          payload.limits.push({
            name: 'UIM',
            type: 'POLICY',
            indexNumber: 0,
            limit: this.stripFormat(this.form.underinsuredMotorist)
          });
        }
      }
      const isValid = x => typeof x === 'string' && x.length > 0;

      payload.limits = payload.limits.reduce((acc, obj) => {
        if (!isValid(obj.limit)) {
          if (obj.name === 'UIM') {
            const biIndex = this.selectsCoverage[this.selectedState].indexOf(
              this.form.bodilyInjury
            );

            obj.limit =
              biIndex !== -1
                ? this.stripFormat(
                    this.selectsCoverage[this.selectedState]
                      .underinsuredMotorist[biIndex]
                  )
                : null;
          }
          obj.limit = null;
        }
        if (obj.limit != null) acc.push(obj);
        return acc;
      }, []);

      if (
        form.drivers[0].hasAutoInsurance.value === 3 ||
        form.drivers[0].hasAutoInsurance.value === 1
      ) {
        payload['priorBiLimit'] = form.drivers[0].recentCoverageLimits.value;
      }
      if (form.info.mobilePhoneNumber) {
        payload.phoneNumbers.push({
          type: 'Mobile',
          number: form.info.mobilePhoneNumber
        });
      }
      if (!payload.isGarageSameAsMailing) {
        let garagingAddress = {
          state: form.info.garagingState.value,
          zip: form.info.garagingZipCode,
          city: form.info.garagingCity,
          streetName: form.info.garagingStreetAddressOne,
          type: 'Garaging'
        };
        payload.ratedState = form.info.garagingState.value;
        payload.addresses.push(garagingAddress);
      }
      if (form.drivers[0].previousExpirationDate) {
        payload.previousExpirationDate = moment(
          form.drivers[0].previousExpirationDate,
          'MM/DD/YYYY'
        ).format();
      }
      if (form.info.hearAbout.value === 'Dealer') {
        payload.referralDealer = form.info.dealer;
      }
      return payload;
    },
    getTotalMonthlyCost() {
      if (
        this.quoteResponse &&
        this.quoteResponse.policies[0] &&
        this.quoteResponse.policies[0].estimatedMonthlyPayment
      ) {
        return Number.parseFloat(
          this.quoteResponse.policies[0].estimatedMonthlyPayment
        );
      } else if (
        this.quoteResponse &&
        this.quoteResponse.policies[0] &&
        this.quoteResponse.policies[0].findBilling
      ) {
        return Number.parseFloat(
          this.quoteResponse.policies[0].findBilling.estimatedMonthlyPayment
        );
      }
    },
    quoteFormCoverageWatch() {
      return _.cloneDeep(this.quoteForm.coverage);
    }
  },
  methods: {
    ...mapMutations([
      'updateForm',
      'updateQuoteRequest',
      'updateVehicleImageResponse',
      'updateQuoteResponse',
      'toggleLoader'
    ]),
    ...mapActions(['requestQuote', 'getVehicleImage']),
    floodlightQuote() {
      if (
        process.env.VUE_APP_ENV.toLowerCase() !== 'production' &&
        process.env.VUE_APP_ENV.toLowerCase() !== 'prod'
      ) {
        return;
      } else if (!canUseAdvertising()) {
        return;
      } else {
        // console.log('2025 Floodlight 4 Active');
        // Porsche PAI Receive a quote Form Continue Button 4.Your Coverage/Quote
        let flDiv = document.body.appendChild(document.createElement('div'));
        let GDPR = '${GDPR}';
        let GDPR_CONSENT_755 = '${GDPR_CONSENT_755}';
        flDiv.setAttribute('id', process.env.VUE_APP_FLOODLIGHT_ID);
        flDiv.style.position = 'absolute';
        flDiv.style.top = '0';
        flDiv.style.left = '0';
        flDiv.style.width = '1px';
        flDiv.style.height = '1px';
        flDiv.style.display = 'none';
        flDiv.innerHTML = `
            <img src="https://ad.doubleclick.net/ddm/activity/src=5368208;type=2025p0;cat=porsc002;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=1?" width="1" height="1" alt=""/>
          `;
        if (process.env.VUE_APP_GOOGLE_ANALYTICS === 'enable') {
          safeGtag('advertising', 'event', 'conversion', {
            allow_custom_scripts: true,
            send_to: 'DC-5368208/2025p0/porsc002+standard'
          });
        }
      }
    },
    toggleUnlimited(index) {
      this.quoteForm.vehicles[index].unlimitedVehicle = !this.quoteForm
        .vehicles[index].unlimitedVehicle;
      this.isUnlimited = this.quoteForm.vehicles[index].unlimitedVehicle;
    },
    setVehicleCoverages(field) {
      if (
        this.quoteForm.vehicles &&
        this.quoteForm.vehicles.length > 1 &&
        this.coverageStepIndex === 0 &&
        field
      ) {
        this.quoteForm.vehicles.forEach((vehicle, index) => {
          if (!this.form[field]) {
            throw new Error(`Form field ${field} does not exist`);
          }

          if (!Array.isArray(this.form[field])) {
            throw new Error(`Form field ${field} is not an array`);
          }

          if (this.form[field].length === 0) {
            throw new Error(`Form field ${field} has a length of zero`);
          }

          if (!this.form[field][index]) {
            throw new Error(`Form field ${field} has no index ${index}`);
          }

          this.form[field][index] = this.form[field][0];
        });
      }
    },
    updateField(field, index, value) {
      if ((!Array.isArray(this.form[field]) && !value) || index < 0) {
        return;
      }
      if (Array.isArray(this.form[field])) {
        this.form[field][index] = value;
        this.form[field] = [...this.form[field]];
        if (
          field === 'comprehensiveDeductible' ||
          field === 'collisionDeductible'
        ) {
          this.setValueForMultipleCoverage(field);
        }
      } else {
        this.form[field] = value;
      }
      if (field === 'bodilyInjury') {
        this.bodilyInjuryChange(value);
      } else if (field === 'uninsuredBodilyInjury') {
        this.uninsuredBodilyInjuryChange(value);
      } else {
        this.updateQuoteResponseToNull();
      }
    },

    FLOOD3() {
      if (
        process.env.VUE_APP_ENV.toLowerCase() !== 'production' &&
        process.env.VUE_APP_ENV.toLowerCase() !== 'prod'
      ) {
        return;
      }
      if (!canUseAdvertising()) {
        return;
      }
      // console.log('Floodlight 3 Active');
      // USA_Porsche_DCM_Floodlight_PAI_QuoteSubmission_PageLoad
      let axel = Math.random() + '';
      let a = axel * 10000000000000;
      let flDiv = document.body.appendChild(document.createElement('div'));
      let GDPR = '${GDPR}';
      let GDPR_CONSENT_755 = '${GDPR_CONSENT_755}';
      flDiv.setAttribute('id', process.env.VUE_APP_FLOODLIGHT_ID);
      flDiv.style.position = 'absolute';
      flDiv.style.top = '0';
      flDiv.style.left = '0';
      flDiv.style.width = '1px';
      flDiv.style.height = '1px';
      flDiv.style.display = 'none';
      flDiv.innerHTML = `<iframe id=${process.env.VUE_APP_FLOODLIGHT_ID +
        '_FLIframe2'} src="https://5368208.fls.doubleclick.net/activityi;src=5368208;type=porsc00c;cat=usa_p002;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=${a}?" width="1" height="1" frameborder="0"></iframe>`;
    },

    recalculate() {
      let firstErrorField;
      this.$validator.validateAll().then(result => {
        if (result) {
          this.FLOOD3();
          const currentIndex = this.coverageStepIndex;
          const maxIndex = this.quoteForm.vehicles.length - 1;
          if (currentIndex < maxIndex) {
            if (
              !this.stateCoverageContinueDisableRules &&
              this.form.comprehensiveDeductible[currentIndex] &&
              this.form.collisionDeductible[currentIndex]
            ) {
              if (
                this.form.comprehensiveDeductible[maxIndex] &&
                this.form.collisionDeductible[maxIndex]
              ) {
                this.coverageStepIndex++;
              } else {
                this.scrollTop();
                this.coverageStepIndex++;
                return;
              }
            } else if (
              this.form.bodilyInjury &&
              this.form.propertyDamage &&
              this.form.uninsuredBodilyInjury &&
              this.form.comprehensiveDeductible[currentIndex] &&
              this.form.collisionDeductible[currentIndex]
            ) {
              if (
                this.form.comprehensiveDeductible[maxIndex] &&
                this.form.collisionDeductible[maxIndex]
              ) {
                this.coverageStepIndex++;
              } else {
                this.scrollTop();
                this.coverageStepIndex++;
                return;
              }
            }
          }
          const requestPayload = this.postPayload;
          let payload = {
            vehicles: _.cloneDeep(this.quoteForm.vehicles),
            coverage: _.cloneDeep(this.form)
          };
          this.updateForm(payload);
          this.updateQuoteRequest(requestPayload);
          this.toggleLoader({
            loaderText:
              'Please wait while we calculate your quote, as this process can take up to a minute.'
          });

          if (
            requestPayload.ratedState === 'AZ' ||
            requestPayload.ratedState === 'CA' ||
            requestPayload.ratedState === 'CO' ||
            requestPayload.ratedState === 'FL' ||
            requestPayload.ratedState === 'GA' ||
            requestPayload.ratedState === 'IL' ||
            requestPayload.ratedState === 'OH' ||
            requestPayload.ratedState === 'OR' ||
            requestPayload.ratedState === 'TN' ||
            requestPayload.ratedState === 'TX' ||
            requestPayload.ratedState === 'UT'
          ) {
            this.requestQuote(requestPayload)
              .then(() => {
                this.calculated = true;
                this.toggleLoader();
              })
              .catch(err => {
                let errorMessage = undefined;
                if (err.error_messages && err.error_messages.length > 1) {
                  errorMessage =
                    err.error_messages[0] + ': ' + err.error_messages[1];
                }
                let error =
                  errorMessage ||
                  `We are working on your quote but need a bit more information.
                Please call customer care at <nobr><a style="text-decoration:underline;color:#fff" href="tel:1-800-400-4128">800-400-4128</a></nobr>
                or let us know a convenient time to call you using our <nobr><a style="text-decoration:underline;color:#fff" href="${window.location.origin}/contact-us">Contact Us</a></nobr> form.`;
                this.toggleLoader();
                this.$snackbar.open({
                  type: 'is-danger',
                  message: error,
                  position: 'is-bottom',
                  indefinite: true
                });
              });
          } else {
            this.toggleLoader({
              close: true
            });
            this.calculated = false;
            let error = `We are working on your quote but need a bit more information.
                    Please call customer care at <nobr><a style="text-decoration:underline;color:#fff" href="tel:1-800-400-4128">800-400-4128</a></nobr>
                    or let us know a convenient time to call you using our <nobr><a style="text-decoration:underline;color:#fff" href="${window.location.origin}/contact-us">Contact Us</a></nobr> form.`;
            this.$snackbar.open({
              type: 'is-danger',
              message: error,
              position: 'is-bottom',
              indefinite: true
            });
          }
        } else {
          firstErrorField = this.$el.querySelector(
            '[name="' + this.$validator.errors.items[0].field + '"]'
          );
          firstErrorField.scrollIntoView();
        }
      });
    },
    previousVehicleClick() {
      if (
        this.coverageStepIndex !== 0 &&
        this.coverageStepIndex < this.quoteForm.vehicles.length
      ) {
        this.coverageStepIndex--;
        this.updateDeductibleDropdown();
        this.updateUnlimited();
      }
    },
    nextVehicleClick() {
      if (this.coverageStepIndex < this.quoteForm.vehicles.length - 1) {
        this.coverageStepIndex++;
        this.updateDeductibleDropdown();
        this.updateUnlimited();
      }
    },
    stripFormat(formattedString) {
      let result = formattedString;
      if (typeof result === 'string') {
        result = result.replace(/\$/g, '');
        result = result.replace(/,/g, '');
        result = result.replace(/( deductible)+/g, '');
        if (result.includes('000 CSL')) {
          result = result.slice(0, 3) + ' CSL';
        }
      }
      return result;
    },
    uninsuredBodilyInjuryChange(val) {
      // this.form.propertyDamageDeductible[this.coverageStepIndex] =
      //   'Coverage Not Applicable';
      const selectedIndex = this.selectsCoverage[
        this.selectedState
      ].uninsuredBodilyInjury.indexOf(val);
      this.form.uninsuredBodilyInjury =
        this.selectsCoverage[this.selectedState].uninsuredBodilyInjury[
          selectedIndex
        ] || 'I decline this coverage';
      if (this.form.uninsuredBodilyInjury === 'I decline this coverage') {
        this.form.uninsuredPropertyDamage = null;
        if (this.selectedState === 'UT') {
          this.form.underinsuredMotorist = 'I decline this coverage';
        }
      } else {
        if (this.selectedState !== 'CA' && this.selectedState !== 'CO') {
          this.form.uninsuredPropertyDamage =
            this.selectsCoverage[this.selectedState].uninsuredPropertyDamage[
              selectedIndex
            ] || null;
        }
        if (this.selectedState === 'UT') {
          this.form.uninsuredPropertyDamage = this.selectsCoverage[
            this.selectedState
          ].uninsuredPropertyDamage[0];
        }
      }
      this.setValueForMultipleCoverage('rentalReimbursement');
    },
    bodilyInjuryChange(val) {
      const selectedIndex = this.selectsCoverage[
        this.selectedState
      ].bodilyInjury.indexOf(val);
      const umbiSelectedIndex = this.selectsCoverage[
        this.selectedState
      ].uninsuredBodilyInjury.indexOf(this.form.uninsuredBodilyInjury);

      // PD
      if (this.selectedState !== 'FL') {
        this.form.propertyDamage = this.selectsCoverage[
          this.selectedState
        ].propertyDamage[selectedIndex];
      }
      // UM(BI)
      if (
        umbiSelectedIndex > selectedIndex ||
        val === 'I decline this coverage'
      ) {
        this.form.uninsuredBodilyInjury = null;
      } else {
        this.form.uninsuredBodilyInjury = this.selectsCoverage[
          this.selectedState
        ].uninsuredBodilyInjury[selectedIndex];
      }

      // UIM
      if (
        this.selectedState === 'UT' &&
        (this.form.uninsuredBodilyInjury === null ||
          this.form.uninsuredBodilyInjury === 'I decline this coverage')
      ) {
        this.form.underinsuredMotorist = 'I decline this coverage';
      } else if (
        this.selectsCoverage[this.selectedState].underinsuredMotorist &&
        this.selectsCoverage[this.selectedState].underinsuredMotorist[
          selectedIndex
        ]
      ) {
        this.form.underinsuredMotorist = this.selectsCoverage[
          this.selectedState
        ].underinsuredMotorist[selectedIndex];
        if (this.selectedState === 'WI') {
          this.form.underinsuredMotorist = this.selectsCoverage[
            this.selectedState
          ].underinsuredMotorist[selectedIndex - 1];
        }
      }
      // UMPD
      if (
        this.selectsCoverage[this.selectedState].uninsuredPropertyDamage &&
        this.selectsCoverage[this.selectedState].uninsuredPropertyDamage[
          selectedIndex
        ] &&
        this.selectedState !== 'CO'
      ) {
        this.form.uninsuredPropertyDamage = this.selectsCoverage[
          this.selectedState
        ].uninsuredPropertyDamage[selectedIndex];
      }
      // PIP
      if (
        this.selectsCoverage[this.selectedState].personalInjuryProtection &&
        this.selectsCoverage[this.selectedState].personalInjuryProtection[
          selectedIndex
        ]
      ) {
        this.form.personalInjuryProtection = this.selectsCoverage[
          this.selectedState
        ].personalInjuryProtection[selectedIndex]
          ? this.selectsCoverage[this.selectedState].personalInjuryProtection[
              selectedIndex
            ]
          : null;
        if (this.selectedState === 'GA') {
          this.form.personalInjuryProtection = this.selectsCoverage[
            this.selectedState
          ].personalInjuryProtection[4];
        }
      }
      // RENT
      this.form.rentalReimbursement[this.coverageStepIndex] = this
        .selectsCoverage[this.selectedState].rentalReimbursement[selectedIndex]
        ? this.selectsCoverage[this.selectedState].rentalReimbursement[
            selectedIndex
          ]
        : null;
      // BI
      this.form.bodilyInjury = this.selectsCoverage[
        this.selectedState
      ].bodilyInjury[selectedIndex];
      this.setValueForMultipleCoverage('rentalReimbursement');
    },
    navigateForward() {
      this.toggleLoader({
        loaderText:
          'Please wait while we finalize your quote, as this process can take up to a minute.'
      });
      if (
        this.quoteResponse.policies[0] &&
        this.quoteResponse.policies[0].quoteRequest
      ) {
        const existing = _.cloneDeep(
          this.quoteResponse.policies[0].quoteRequest
        );
        const newQuote = _.cloneDeep(this.postPayload);
        delete newQuote.creditScore;
        delete newQuote.startResponseTime;
        delete newQuote.isValid;
        // console.log('match? ', existing === newQuote);
        // console.log('existing ', JSON.stringify(existing));
        // console.log('newq ', JSON.stringify(newQuote));
        if (JSON.stringify(existing) === JSON.stringify(newQuote)) {
          this.toggleLoader();
          this.$emit('nextButtonAction');
        }
      }
      const requestPayload = this.postPayload;
      requestPayload.vehicles.forEach((vehicle, index) => {
        const quoteFormVehicles = this.quoteForm.vehicles;

        vehicle.billingType = quoteFormVehicles[index].unlimitedVehicle
          ? 'capped'
          : 'per_mile';
        vehicle.mileage.miles = quoteFormVehicles[index].unlimitedVehicle
          ? 14600
          : parseInt(quoteFormVehicles[index].miles) > 14600
          ? 14600
          : parseInt(quoteFormVehicles[index].miles);
      });
      let payload = {
        coverage: _.cloneDeep(this.form)
      };
      this.updateForm(payload);
      this.requestQuote(requestPayload)
        .then(() => {
          this.toggleLoader();
          this.$emit('nextButtonAction'); // to trigger validation
        })
        .catch(err => {
          this.scrollTop();
          let error =
            err.response.data.error_messages &&
            err.response.data.error_messages.length > 1
              ? err.response.data.error_messages[0] +
                ': ' +
                err.response.data.error_messages[1]
              : `The system is unable to complete your request at this time.
                Please call <nobr><a style="text-decoration:underline;color:#fff" href="tel:1-800-400-4128">800-400-4128</a></nobr>
                to speak to a customer service concierge.`;
          this.toggleLoader();
          this.$snackbar.open({
            type: 'is-danger',
            message: error,
            position: 'is-bottom',
            indefinite: true
          });
        });
    },
    backButtonNavigation() {
      let firstErrorField;
      this.$validator.validateAll().then(result => {
        if (result) {
          this.scrollTop();
          // if (
          //   this.coverageStepIndex !== 0 &&
          //   this.coverageStepIndex < this.quoteForm.vehicles.length
          // ) {
          //   this.coverageStepIndex = this.coverageStepIndex - 1;
          //   this.updateDeductibleDropdown();
          // } else {
          this.$emit('goBack');
          this.$destroy();
          // }
        } else {
          firstErrorField = this.$el.querySelector(
            '[name="' + this.$validator.errors.items[0].field + '"]'
          );
          firstErrorField.scrollIntoView(false);
        }
      });
    },
    submit() {
      let firstErrorField;
      return new Promise((resolve, reject) => {
        this.$validator.validateAll().then(result => {
          if (result) {
            let payload = {
              vehicles: _.cloneDeep(this.quoteForm.vehicles),
              coverage: _.cloneDeep(this.form)
            };
            this.updateForm(payload);
            resolve(true);
          } else {
            firstErrorField = this.$el.querySelector(
              '[name="' + this.$validator.errors.items[0].field + '"]'
            );
            firstErrorField.focus();

            reject('invalid fields');
          }
        });
      });
    },
    getVehicleImageFromResponse(response) {
      if (
        response &&
        response.Vin &&
        response.Vin.picture &&
        response.Vin.picture
      ) {
        let vehicleImg = response.Vin.picture.filter(img => {
          return img._attributes.view === 'extcam02';
        });
        if (vehicleImg && vehicleImg.length) {
          return vehicleImg[0]._attributes.uri;
        } else if (response.Vin.picture[0]) {
          return response.Vin.picture[0]._attributes.uri;
        }
      }
    },
    fetchVehicleImage() {
      this.getVehicleImage({
        vin: this.quoteForm.vehicles[this.coverageStepIndex].vin,
        year: this.quoteForm.vehicles[this.coverageStepIndex].year,
        model: this.quoteForm.vehicles[this.coverageStepIndex].model
      });
    },
    continueNavigation() {
      if (
        this.form.bodilyInjury &&
        this.form.comprehensiveDeductible.length &&
        this.form.collisionDeductible.length &&
        this.form.policyEffectiveDate
      ) {
        if (!this.quoteResponse) {
          this.toggleLoader({
            loaderText:
              'Please wait while we calculate your quote, as this process can take up to a minute.'
          });
          const requestPayload = this.postPayload;
          let payload = {
            coverage: _.cloneDeep(this.form)
          };
          this.updateForm(payload);
          this.requestQuote(requestPayload)
            .then(() => {
              this.toggleLoader();
            })
            .catch(err => {
              let error =
                err.response.data.error_messages &&
                err.response.data.error_messages.length > 1
                  ? err.response.data.error_messages[0] +
                    ': ' +
                    err.response.data.error_messages[1]
                  : `The system is unable to complete your request at this time.
                Please call <nobr><a style="text-decoration:underline;color:#fff" href="tel:1-800-400-4128">800-400-4128</a></nobr>
                to speak to a customer service concierge.`;
              this.toggleLoader();
              this.$snackbar.open({
                type: 'is-danger',
                message: error,
                position: 'is-bottom',
                indefinite: true
              });
            });
        } else {
          if (this.coverageStepIndex < this.quoteForm.vehicles.length - 1) {
            this.coverageStepIndex = this.coverageStepIndex + 1;
            this.fetchVehicleImage();
            this.updateDeductibleDropdown();
          } else {
            this.$emit('nextButtonAction'); // to proceed to next page
          }
        }
      } else {
        this.$emit('nextButtonAction'); // to trigger validtion
      }
    },
    updateUnlimited() {
      if (
        this.quoteForm.vehicles[this.coverageStepIndex].unlimitedVehicle !==
        this.isUnlimited
      ) {
        this.isUnlimited = this.quoteForm.vehicles[
          this.coverageStepIndex
        ].unlimitedVehicle;
      }
    },
    updateDeductibleDropdown() {
      if (
        this.quoteForm.vehicles[this.coverageStepIndex].leaseOwnFinance
          .value === 'owned'
      ) {
        if (
          !this.selectsCoverage[
            this.selectedState
          ].comprehensiveDeductible.includes('I decline this coverage') &&
          !this.selectsCoverage[
            this.selectedState
          ].collisionDeductible.includes('I decline this coverage')
        ) {
          this.selectsCoverage[this.selectedState].comprehensiveDeductible.push(
            'I decline this coverage'
          );
          this.selectsCoverage[this.selectedState].collisionDeductible.push(
            'I decline this coverage'
          );
        }
      } else {
        if (
          this.selectsCoverage[
            this.selectedState
          ].comprehensiveDeductible.includes('I decline this coverage')
        ) {
          this.selectsCoverage[
            this.selectedState
          ].comprehensiveDeductible.pop();
          this.selectsCoverage[this.selectedState].collisionDeductible.pop();
        }
      }
    },
    setValueForMultipleCoverage(field) {
      if (
        this.quoteForm.vehicles[this.coverageStepIndex].leaseOwnFinance
          .value === 'owned' &&
        (field === 'comprehensiveDeductible' || field === 'collisionDeductible')
      ) {
        let otherDeductible;
        if (field === 'comprehensiveDeductible') {
          otherDeductible = 'collisionDeductible';
        } else if (field === 'collisionDeductible') {
          otherDeductible = 'comprehensiveDeductible';
        }
        if (
          this.form[field][this.coverageStepIndex] === 'I decline this coverage'
        ) {
          if (this.selectedState === 'FL' && field === 'collisionDeductible') {
            this.showDeductibleWarningModal = false;
          } else {
            this.showDeductibleWarningModal = true;
            this.form[otherDeductible][this.coverageStepIndex] =
              'I decline this coverage';
          }
          this.form.rentalReimbursement[this.coverageStepIndex] =
            'I decline this coverage';
        } else if (
          this.form[field][this.coverageStepIndex] !==
            'I decline this coverage' &&
          this.form[otherDeductible][this.coverageStepIndex] ===
            'I decline this coverage'
        ) {
          this.form[otherDeductible][this.coverageStepIndex] = null;
        }
      }
      if (field === 'rentalReimbursement') {
        this.quoteForm.vehicles.forEach((veh, index) => {
          this.setValueForVehicleArray('rentalReimbursement', index);
        });
      }
      let payload = {
        coverage: _.cloneDeep(this.form)
      };
      this.updateForm(payload);
    },
    scrollTop() {
      let container = document.getElementById('quote-container');
      setTimeout(() => {
        container.scrollIntoView(true);
      }, 100);
    },
    updateQuoteResponseToNull() {
      this.updateQuoteResponse(null);
    },
    setValueForVehicleArray(field, index) {
      if (this.form[field][0] === 'I decline this coverage') {
        this.form[field][index] = this.selectsCoverage[this.selectedState][
          field
        ][2];
      } else {
        this.form[field][index] = this.form[field][0];
      }
      return this.form[field][index];
    },
    resetSelectedCoverages() {
      this.form.bodilyInjury = null;
      this.form.propertyDamage = null;
      this.form.comprehensiveDeductible = [];
      this.form.collisionDeductible = [];
      this.form.propertyDamageDeductible = [];
      this.form.uninsuredCoverageType = 'R';
      this.form.uninsuredBodilyInjury = null;
      this.form.uninsuredPropertyDamage = null;
      this.form.personalInjuryProtection = null;
      this.form.rentalReimbursement = [];
      this.form.policyEffectiveDate = moment().format('MM/DD/YYYY');
      if (this.quoteResponse) {
        this.updateQuoteResponseToNull();
      }
      setTimeout(() => {
        this.errors.clear();
      }, 50);
    },
    clickedCloseOnModal() {
      this.showDeductibleWarningModal = false;
    }
  },
  watch: {
    selectedState: {
      immediate: true,
      handler: function(newValue, oldValue) {
        if (newValue && oldValue && !_.isEqual(newValue, oldValue)) {
          this.resetSelectedCoverages();
        }
      }
    },
    quoteFormCoverageWatch: {
      immediate: true,
      handler: function(newValue, oldValue) {
        if (newValue && oldValue && !_.isEqual(newValue, oldValue)) {
          this.form = _.cloneDeep(this.quoteForm.coverage);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.active {
  color: $primary;
}
.inactive {
  color: lightgrey;
}
.disabled {
  background-color: #b5b4ba;
}
.disabled__button__color {
  color: #ffffff !important;
}
button {
  border: none;
}
.box {
  padding: 0 1.5rem 1.5rem;

  @media (min-width: 1000px) {
    padding: 0 2rem 2rem;
  }

  @media (min-width: 1400px) {
    padding: 0 3rem 3.5rem;
  }
}

.heading {
  font-family: $family-sans-serif-bold;
  text-transform: none;
  letter-spacing: 0;
  font-size: inherit;
  margin-bottom: 1rem;

  &:last-of-type {
    border-top: 1px solid $mischka;
    margin-top: 2rem;
    padding-top: 2rem;
  }
}

.right-section-first-container {
  border-top: none !important;
  margin-top: 0 !important;
}

.note {
  color: $mischka;
}

.input__field {
  min-height: 3.7rem;
}

.input__field:disabled {
  color: #737278;
}

.effective-date-field {
  margin-top: 1.75rem;
  @media (max-width: 480px) {
    margin-top: 1rem;
  }
}

@media (max-width: 760px) {
  .quote-wizard .button-primary + .button-primary.third-button {
    margin-top: 1rem;
    margin-left: 0;
  }
}
.tool-tip {
  color: #737278;
  top: 2px;
  left: 2px;
  .content {
    @media (min-width: 992px) {
      width: 420px;
    }
    @media (max-width: 991px) {
      width: 415px;
      left: 8px;
      i {
        left: 50%;
      }
    }
    @media (min-width: 992px) {
      left: 50%;
    }
    @media (max-width: 760px) {
      width: 300px;
      left: -15px;
      i {
        left: 57%;
      }
    }
  }
}
.multi-vehicle-chevron {
  display: flex;

  &__right {
    position: absolute;
    top: 100px;
    right: 12px;

    @media (min-width: 1000px) {
      right: 15px;
    }

    @media (min-width: 1400px) {
    }
  }
  &__left {
    position: absolute;
    top: 100px;
    left: 12px;

    @media (min-width: 1000px) {
      left: 15px;
    }

    @media (min-width: 1400px) {
    }
  }
}
.button-container {
  .button-left-align {
    display: flex;
    margin-top: 2rem;
    justify-content: flex-start;
  }

  .button-right-align {
    display: flex;
    margin-top: 2rem;
    justify-content: flex-end;
  }
}
</style>
